import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, MinusOutlined } from '@ant-design/icons';

import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useActions } from '../../hooks/useActions';
import { useResponsive } from '../../hooks/useResponsive';
import { IGradingScalesState } from '../../store/reducers/grading_scales/types';
import { transformToAPI } from '../../utils/gradingScaleTransform';

import { PageTitle } from '../../components/common/PageTitle/PageTitle';
import { PageContainer } from '../../components/layouts/PageLayouts/PageContainer/PageContainer';
import { ContentCard } from '../../components/layouts/PageLayouts/ContentCard/ContentCard';
import { BusinessButton } from '../../components/common/buttons/BusinessButton/BusinessButton';
import { notificationController } from '../../controllers/notificationController';

// Styled Components
const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  .title {
    font-size: 24px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &::before {
      content: '🎓';
      font-size: 28px;
    }
  }
  
  .subtitle {
    font-size: 14px;
    color: rgba(44, 62, 80, 0.7);
    margin: 4px 0 0 36px;
  }
  
  @media (max-width: 768px) {
    .title {
      font-size: 20px;
      
      &::before {
        font-size: 24px;
      }
    }
    
    .subtitle {
      margin-left: 32px;
      font-size: 13px;
    }
  }
`;

const ActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 12px;
  }
`;

const SearchInput = styled.div`
  position: relative;
  min-width: 280px;
  
  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
  
  input {
    width: 100%;
    padding: 10px 12px 10px 40px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    font-size: 14px;
    background: white;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #1677ff;
      box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
    }
    
    &::placeholder {
      color: rgba(0, 0, 0, 0.4);
    }
  }
  
  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
  }
`;

const CreateButton = styled(BusinessButton)`
  min-width: 160px;
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: auto;
  }
`;

// Grading Scales Cards Styles
const ScalesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 16px;
  }
`;

const ScaleCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    border-color: rgba(22, 119, 255, 0.2);
    
    .actions {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #4096ff 0%, #1677ff 50%, #0958d9 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  /* Mobile & tablet - no hover effects, always show top border */
  @media (max-width: 1024px) {
    padding: 20px;
    
    &:hover {
      transform: none;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
    
    &::before {
      opacity: 1;
    }
    
    &:active {
      transform: scale(0.98);
    }
  }
`;

const ScaleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  
  .scale-name {
    font-size: 18px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 4px 0;
    line-height: 1.3;
  }
  
  .scale-type {
    font-size: 13px;
    color: rgba(44, 62, 80, 0.6);
    background: rgba(64, 150, 255, 0.1);
    padding: 4px 8px;
    border-radius: 6px;
    font-weight: 500;
  }
`;

const ScaleInfo = styled.div`
  margin-bottom: 16px;
  
  .created-date {
    font-size: 13px;
    color: rgba(44, 62, 80, 0.6);
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    
    &::before {
      content: '📅';
      font-size: 14px;
    }
  }
`;

const GradesContainer = styled.div`
  margin-bottom: 20px;
  
  .grades-label {
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
  }
  
  .grades-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const GradeBadge = styled.span<{ grade: string }>`
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  background: ${props => {
    const grade = props.grade.toLowerCase();
    if (grade.includes('a') || grade.includes('90')) return '#10B981';
    if (grade.includes('b') || grade.includes('80')) return '#3B82F6';
    if (grade.includes('c') || grade.includes('70')) return '#F59E0B';
    if (grade.includes('d') || grade.includes('60')) return '#EF4444';
    if (grade.includes('f') || grade.includes('50')) return '#6B7280';
    return '#8B5CF6';
  }};
  
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`;

const CardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
  
  &.actions {
    opacity: 0;
  }
  
  /* Always show on mobile and tablet */
  @media (max-width: 1024px) {
    opacity: 1;
    transform: translateY(0);
    
    &.actions {
      opacity: 1;
    }
  }
`;

const ActionButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  ${props => props.variant === 'edit' ? `
    background: rgba(139, 92, 246, 0.1);
    color: #8B5CF6;
    
    &:hover {
      background: rgba(139, 92, 246, 0.2);
      transform: translateY(-1px);
    }
  ` : `
    background: rgba(239, 68, 68, 0.1);
    color: #EF4444;
    
    &:hover {
      background: rgba(239, 68, 68, 0.2);
      transform: translateY(-1px);
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(0, 0, 0, 0.45);
  
  .icon {
    font-size: 64px;
    margin-bottom: 16px;
    display: block;
  }
  
  .title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
    color: rgba(0, 0, 0, 0.65);
  }
  
  .description {
    font-size: 14px;
    margin-bottom: 24px;
  }
`;

const LoadingState = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const SkeletonCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  
  .skeleton-line {
    height: 12px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 6px;
    margin-bottom: 12px;
    
    &.title {
      height: 20px;
      width: 70%;
    }
    
    &.subtitle {
      height: 14px;
      width: 40%;
    }
    
    &.badge {
      height: 24px;
      width: 80px;
      display: inline-block;
      margin-right: 8px;
    }
  }
  
  @keyframes loading {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 20px;
  isolation: isolate;
`;

const ModalContent = styled.div`
  position: relative;
  z-index: 100000;
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  
  .title {
    font-size: 20px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &::before {
      content: '🎓';
      font-size: 24px;
    }
  }
  
  .subtitle {
    font-size: 14px;
    color: rgba(44, 62, 80, 0.7);
    margin: 0 0 20px 0;
  }
  
  .close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.4);
    transition: color 0.2s ease;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    
    &:hover {
      color: rgba(0, 0, 0, 0.8);
      background: rgba(0, 0, 0, 0.05);
    }
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
  
  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
    margin: 0 0 16px 0;
  }
`;

const FormField = styled.div`
  margin-bottom: 20px;
  
  .field-label {
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
    display: block;
  }
  
  .field-input {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #1677ff;
      box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
    }
    
    &::placeholder {
      color: rgba(0, 0, 0, 0.4);
    }
  }
  
  .field-error {
    color: #EF4444;
    font-size: 12px;
    margin-top: 4px;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #1677ff;
    background: rgba(22, 119, 255, 0.02);
  }
  
  input[type="radio"] {
    margin: 0;
    accent-color: #1677ff;
  }
  
  .option-label {
    font-size: 14px;
    font-weight: 500;
    color: #2c3e50;
  }
  
  .option-description {
    font-size: 12px;
    color: rgba(44, 62, 80, 0.6);
  }
  
  &:has(input:checked) {
    border-color: #1677ff;
    background: rgba(22, 119, 255, 0.05);
  }
`;

const GradeTable = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  overflow: hidden;
  background: white;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 70px 80px 80px 1fr 40px;
  gap: 8px;
  background: rgba(248, 250, 252, 0.8);
  padding: 12px 16px;
  font-weight: 600;
  font-size: 13px;
  color: #2c3e50;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  align-items: center;
  
  > div {
    text-align: center;
  }
  
  > div:nth-child(4) {
    text-align: left;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 55px 65px 65px 1fr 36px;
    padding: 10px 12px;
    font-size: 12px;
    gap: 6px;
  }
`;

const TableRow = styled.div<{ hasError?: boolean }>`
  display: grid;
  grid-template-columns: 70px 80px 80px 1fr 40px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  align-items: center;
  gap: 8px;
  background: ${props => props.hasError ? 'rgba(239, 68, 68, 0.02)' : 'white'};
  border-left: ${props => props.hasError ? '3px solid #EF4444' : '3px solid transparent'};
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 55px 65px 65px 1fr 36px;
    padding: 10px 12px;
    gap: 6px;
  }
  
  .grade-input {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 6px;
    font-size: 13px;
    text-align: center;
    font-weight: 600;
    
    &:focus {
      outline: none;
      border-color: #1677ff;
      box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
    }
    
    &.error {
      border-color: #EF4444;
      background: rgba(239, 68, 68, 0.05);
    }
  }
  
  .range-input {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 6px;
    font-size: 13px;
    text-align: center;
    
    &:focus {
      outline: none;
      border-color: #1677ff;
      box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
    }
    
    &.error {
      border-color: #EF4444;
      background: rgba(239, 68, 68, 0.05);
    }
  }
  
  .description-input {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 6px;
    font-size: 13px;
    
    &:focus {
      outline: none;
      border-color: #1677ff;
      box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
    }
  }
  
  .remove-btn {
    background: rgba(239, 68, 68, 0.1);
    border: none;
    border-radius: 6px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #EF4444;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(239, 68, 68, 0.2);
      transform: scale(1.05);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }
`;

const AddRowButton = styled.button`
  width: 100%;
  padding: 12px;
  border: 2px dashed rgba(22, 119, 255, 0.3);
  border-radius: 8px;
  background: rgba(22, 119, 255, 0.02);
  color: #1677ff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  
  &:hover {
    border-color: #1677ff;
    background: rgba(22, 119, 255, 0.05);
  }
`;

const ValidationMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
  color: #EF4444;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '⚠️';
    font-size: 16px;
  }
`;

const ModalFooter = styled.div`
  padding: 20px 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: rgba(248, 250, 252, 0.5);
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 8px;
  }
`;

const FooterButton = styled(BusinessButton)`
  min-width: 100px;
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: auto;
  }
`;

// Visualization Preview Styles
const PreviewSection = styled.div`
  margin-top: 24px;
  padding: 20px;
  background: rgba(248, 250, 252, 0.6);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  
  .preview-title {
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &::before {
      content: '👁️';
      font-size: 18px;
    }
  }
  
  .preview-subtitle {
    font-size: 13px;
    color: rgba(44, 62, 80, 0.6);
    margin-bottom: 20px;
  }
`;

const GradientBar = styled.div`
  display: flex;
  height: 50px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #e5e5e5;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const GradeSegment = styled.div<{ 
  color: string;
  isValid: boolean;
}>`
  width: 100%;
  height: 100%;
  background: ${props => props.isValid ? props.color : 'rgba(239, 68, 68, 0.8)'};
  border-right: 2px solid rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  
  &:hover {
    filter: brightness(1.1);
    z-index: 2;
  }
  
  &:last-child {
    border-right: none;
  }
  
  .grade-label {
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    font-size: 16px;
  }
`;

const TooltipContainer = styled.div<{ width?: number }>`
  position: relative;
  flex: ${props => props.width || 1};
  min-width: 40px;
  height: 100%;
`;

const Tooltip = styled.div<{ visible: boolean }>`
  position: absolute;
  bottom: 120%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
  z-index: 10;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
`;

const PreviewStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 16px;
`;

const StatItem = styled.div`
  text-align: center;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  
  .stat-value {
    font-size: 18px;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 4px;
  }
  
  .stat-label {
    font-size: 12px;
    color: rgba(44, 62, 80, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const EmptyPreview = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(0, 0, 0, 0.4);
  
  .icon {
    font-size: 48px;
    margin-bottom: 12px;
    display: block;
  }
  
  .message {
    font-size: 14px;
  }
`;

// Delete Modal Styles
const DeleteModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  padding: 32px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const DeleteModalIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const DeleteModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 12px 0;
`;

const DeleteModalText = styled.p`
  font-size: 14px;
  color: rgba(44, 62, 80, 0.7);
  margin: 0 0 24px 0;
  line-height: 1.6;
  
  strong {
    color: #2c3e50;
  }
`;

const DeleteModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  
  @media (max-width: 480px) {
    flex-direction: column-reverse;
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #EF4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #DC2626;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const GradingScalesPage: React.FC = () => {
  const { isDesktop } = useResponsive();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Redux state
  const grading_scales = useTypedSelector<any>(state => state.grading_scales);
  const { selectedGradingScale, selectedId } = useTypedSelector<IGradingScalesState>(state => state.grading_scales);
  const { deleteGradingScaleRequest, getGradingScalesRequest, createGradingScaleRequest, updateGradingScaleRequest } = useActions();
  const { success: deleteSuccess } = useTypedSelector<IGradingScalesState>(state => state.grading_scales).delete;
  const { success: createSuccess, error: createError, loading: createLoading } = useTypedSelector<IGradingScalesState>(state => state.grading_scales).create;
  const { success: updateSuccess, error: updateError } = useTypedSelector<IGradingScalesState>(state => state.grading_scales).update;

  // Local state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [editingScaleId, setEditingScaleId] = useState<number | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [deletingScale, setDeletingScale] = useState<{ id: number; name: string } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Form state
  interface GradeRange {
    id: string;
    grade: string;
    min: string;
    max: string;
    description: string;
  }

  interface FormData {
    scaleName: string;
    scaleType: 'percentage' | 'points' | 'custom';
    gradeRanges: GradeRange[];
  }

  const [formData, setFormData] = useState<FormData>({
    scaleName: '',
    scaleType: 'percentage',
    gradeRanges: [
      { id: '1', grade: 'A', min: '90', max: '100', description: 'Excellent' },
      { id: '2', grade: 'B', min: '80', max: '89', description: 'Good' },
      { id: '3', grade: 'C', min: '70', max: '79', description: 'Fair' },
      { id: '4', grade: 'D', min: '60', max: '69', description: 'Poor' },
      { id: '5', grade: 'F', min: '0', max: '59', description: 'Fail' }
    ]
  });

  interface FormErrors {
    scaleName?: string;
    scaleType?: string;
    gradeRanges?: { [key: string]: string };
    general?: string;
  }

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);

  // Get real grading scales from Redux store

  // Handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCreateScale = () => {
    setCreateModalVisible(true);
  };

  const handleCreateModalClose = () => {
    setCreateModalVisible(false);
    resetForm();
  };

  const handleCreateSuccess = () => {
    setCreateModalVisible(false);
      getGradingScalesRequest();
    notificationController.success({
      message: 'Success',
      description: 'Grading scale created successfully!'
    });
  };

  const handleEditScale = (scaleId: number) => {
    // Find the scale to edit
    const scaleToEdit = grading_scales.gradingScales?.find((s: any) => s.id === scaleId);
    if (!scaleToEdit) return;
    
    // Pre-fill form with scale data
    const typeMapping: { [key: string]: 'percentage' | 'points' | 'custom' } = {
      'Percentage': 'percentage',
      'Points': 'points',
      'Custom': 'custom'
    };
    
    setFormData({
      scaleName: scaleToEdit.name || '',
      scaleType: typeMapping[scaleToEdit.type] || 'percentage',
      gradeRanges: (scaleToEdit.ranges || []).map((range: any, index: number) => ({
        id: range.id?.toString() || Date.now().toString() + index,
        grade: range.grade || '',
        min: range.minValue || '',
        max: range.maxValue || '',
        description: range.description || ''
      }))
    });
    
    setEditingScaleId(scaleId);
    setEditModalVisible(true);
  };

  const handleEditModalClose = () => {
    setEditModalVisible(false);
    setEditingScaleId(null);
    resetForm();
  };

  const handleEditSubmit = () => {
    if (!validateForm() || !editingScaleId) return;
    
    setIsSubmitting(true);
    
    try {
      const apiModel = transformToAPI(formData);
      updateGradingScaleRequest(editingScaleId, apiModel);
    } catch (error) {
      notificationController.error({
        message: 'Error',
        description: 'Failed to update grading scale.'
      });
      setIsSubmitting(false);
    }
  };

  const handleDeleteScale = (scaleId: number, scaleName: string) => {
    setDeletingScale({ id: scaleId, name: scaleName });
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (deletingScale) {
      deleteGradingScaleRequest(deletingScale.id);
    }
  };

  const handleDeleteModalClose = () => {
    setDeleteModalVisible(false);
    setDeletingScale(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  // Filter scales based on search query
  const filteredScales = (grading_scales.gradingScales || []).filter((scale: any) =>
    scale.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    scale.type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Form handlers
  const handleFormFieldChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleGradeRangeChange = (id: string, field: keyof GradeRange, value: string) => {
    setFormData(prev => ({
      ...prev,
      gradeRanges: prev.gradeRanges.map(range =>
        range.id === id ? { ...range, [field]: value } : range
      )
    }));
    
    // Clear range error when user starts typing
    if (formErrors.gradeRanges?.[id]) {
      setFormErrors(prev => {
        const newRangeErrors = { ...prev.gradeRanges };
        delete newRangeErrors[id];
        return { ...prev, gradeRanges: newRangeErrors };
      });
    }
  };

  const addGradeRange = () => {
    const newId = Date.now().toString();
    const newRange: GradeRange = {
      id: newId,
      grade: '',
      min: '',
      max: '',
      description: ''
    };
    
    setFormData(prev => ({
      ...prev,
      gradeRanges: [...prev.gradeRanges, newRange]
    }));
  };

  const removeGradeRange = (id: string) => {
    if (formData.gradeRanges.length <= 1) return; // Keep at least one range
    
    setFormData(prev => ({
      ...prev,
      gradeRanges: prev.gradeRanges.filter(range => range.id !== id)
    }));
    
    // Remove error for this range
    if (formErrors.gradeRanges?.[id]) {
      setFormErrors(prev => {
        const newRangeErrors = { ...prev.gradeRanges };
        delete newRangeErrors[id];
        return { ...prev, gradeRanges: newRangeErrors };
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    
    // Validate scale name
    if (!formData.scaleName.trim()) {
      errors.scaleName = 'Scale name is required';
    }
    
    // Validate grade ranges
    const rangeErrors: { [key: string]: string } = {};
    const usedGrades = new Set<string>();
    const usedDescriptions = new Set<string>();
    const ranges: { min: number; max: number; id: string }[] = [];
    
    formData.gradeRanges.forEach(range => {
      const { id, grade, min, max, description } = range;
      
      // Check required fields
      if (!grade.trim()) {
        rangeErrors[id] = 'Grade is required';
        return;
      }
      
      if (!min.trim() || !max.trim()) {
        rangeErrors[id] = 'Min and Max values are required';
        return;
      }
      
      // Check for duplicate grades
      if (usedGrades.has(grade.toUpperCase())) {
        rangeErrors[id] = `Grade "${grade}" is already used`;
        return;
      }
      usedGrades.add(grade.toUpperCase());
      
      // Check for duplicate descriptions
      if (description.trim()) {
        if (usedDescriptions.has(description.trim().toLowerCase())) {
          rangeErrors[id] = `Description "${description}" is already used`;
          return;
        }
        usedDescriptions.add(description.trim().toLowerCase());
      }
      
      // Validate numeric ranges for percentage and points
      if (formData.scaleType !== 'custom') {
        const minNum = parseFloat(min);
        const maxNum = parseFloat(max);
        
        if (isNaN(minNum) || isNaN(maxNum)) {
          rangeErrors[id] = 'Min and Max must be valid numbers';
          return;
        }
        
        if (minNum >= maxNum) {
          rangeErrors[id] = 'Min must be less than Max';
          return;
        }
        
        ranges.push({ min: minNum, max: maxNum, id });
      }
    });
    
    // Check for overlapping/incorrect ranges (sorted by max DESC - highest first)
    if (formData.scaleType !== 'custom' && ranges.length > 1) {
      // Sort by max value descending (100, 89, 79, 69, 59...)
      ranges.sort((a, b) => b.max - a.max);
      
      for (let i = 0; i < ranges.length - 1; i++) {
        const current = ranges[i]; // Higher range (e.g., 90-100)
        const next = ranges[i + 1]; // Lower range (e.g., 80-89)
        
        // Check if ranges overlap
        if (current.min <= next.max) {
          if (!rangeErrors[current.id]) {
            rangeErrors[current.id] = `Range overlaps: Min (${current.min}) should be > ${next.max}`;
          }
          if (!rangeErrors[next.id]) {
            rangeErrors[next.id] = `Range overlaps: Max (${next.max}) should be < ${current.min}`;
          }
        }
        
        // Check for gaps (warning, not error) - optional
        // A proper scale should have: current.min = next.max + 1
        // E.g., A: 90-100, B: 80-89 (not 80-88)
      }
    }
    
    if (Object.keys(rangeErrors).length > 0) {
      errors.gradeRanges = rangeErrors;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      scaleName: '',
      scaleType: 'percentage',
      gradeRanges: [
        { id: '1', grade: 'A', min: '90', max: '100', description: 'Excellent' },
        { id: '2', grade: 'B', min: '80', max: '89', description: 'Good' },
        { id: '3', grade: 'C', min: '70', max: '79', description: 'Fair' },
        { id: '4', grade: 'D', min: '60', max: '69', description: 'Poor' },
        { id: '5', grade: 'F', min: '0', max: '59', description: 'Fail' }
      ]
    });
    setFormErrors({});
    setIsSubmitting(false);
    setHoveredSegment(null);
  };

  // Preview functions - color based on percentage range
  const getColorByPercentage = (min: number, max: number): string => {
    const avg = (min + max) / 2;
    
    // 90-100% — Green shades
    if (avg >= 95) return '#16A34A'; // Dark green (A+)
    if (avg >= 90) return '#22C55E'; // Light green (A)
    
    // 80-90% — Teal/Cyan shades
    if (avg >= 85) return '#0D9488'; // Dark teal (B+)
    if (avg >= 80) return '#14B8A6'; // Light teal (B)
    
    // 70-80% — Yellow shades
    if (avg >= 75) return '#CA8A04'; // Dark yellow (C+)
    if (avg >= 70) return '#EAB308'; // Light yellow (C)
    
    // 60-70% — Orange shades
    if (avg >= 65) return '#EA580C'; // Dark orange (D+)
    if (avg >= 60) return '#F97316'; // Light orange (D)
    
    // 0-60% — Red shades
    if (avg >= 50) return '#DC2626'; // Light red
    return '#B91C1C'; // Dark red (F)
  };
  
  // Fallback for custom grades (letter-based)
  const getGradeColor = (grade: string, min?: string, max?: string): string => {
    // If we have numeric values, use percentage-based colors
    if (min && max) {
      const minNum = parseFloat(min);
      const maxNum = parseFloat(max);
      if (!isNaN(minNum) && !isNaN(maxNum)) {
        return getColorByPercentage(minNum, maxNum);
      }
    }
    
    // Fallback for custom/letter grades
    const gradeUpper = grade.toUpperCase();
    if (gradeUpper.includes('A+') || gradeUpper === 'A+') return '#16A34A';
    if (gradeUpper.includes('A') || gradeUpper === '5') return '#22C55E';
    if (gradeUpper.includes('B+')) return '#0D9488';
    if (gradeUpper.includes('B') || gradeUpper === '4') return '#14B8A6';
    if (gradeUpper.includes('C+')) return '#CA8A04';
    if (gradeUpper.includes('C') || gradeUpper === '3') return '#EAB308';
    if (gradeUpper.includes('D+')) return '#EA580C';
    if (gradeUpper.includes('D') || gradeUpper === '2') return '#F97316';
    if (gradeUpper.includes('F') || gradeUpper === '1') return '#DC2626';
    if (gradeUpper.includes('PASS')) return '#22C55E';
    if (gradeUpper.includes('FAIL')) return '#DC2626';
    return '#8B5CF6'; // Default purple for unknown
  };

  const calculatePreviewData = () => {
    if (formData.scaleType === 'custom') {
      // For custom scales, show equal segments
      const validRanges = formData.gradeRanges.filter(range => 
        range.grade.trim() && range.description.trim()
      );
      
      return validRanges.map((range, index) => ({
        ...range,
        width: validRanges.length > 0 ? 100 / validRanges.length : 0,
        left: validRanges.length > 0 ? (100 / validRanges.length) * index : 0,
        color: getGradeColor(range.grade, range.min, range.max),
        isValid: true
      }));
    }

    // For percentage and points, calculate based on numeric ranges
    const validRanges = formData.gradeRanges.filter(range => {
      const min = parseFloat(range.min);
      const max = parseFloat(range.max);
      return !isNaN(min) && !isNaN(max) && min < max && range.grade.trim();
    });

    if (validRanges.length === 0) return [];

    // Sort by min value
    const sortedRanges = [...validRanges].sort((a, b) => parseFloat(a.min) - parseFloat(b.min));
    
    // Find the total range
    const totalMin = Math.min(...sortedRanges.map(r => parseFloat(r.min)));
    const totalMax = Math.max(...sortedRanges.map(r => parseFloat(r.max)));
    const totalRange = totalMax - totalMin;

    return sortedRanges.map(range => {
      const min = parseFloat(range.min);
      const max = parseFloat(range.max);
      const width = ((max - min) / totalRange) * 100;
      const left = ((min - totalMin) / totalRange) * 100;
      
      return {
        ...range,
        width,
        left,
        color: getGradeColor(range.grade, range.min, range.max),
        isValid: true
      };
    });
  };

  const previewData = calculatePreviewData();
  const hasValidPreview = previewData.length > 0 && formData.scaleName.trim();

  const handleFormSubmit = () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Transform form data to API format
      const apiModel = transformToAPI(formData);
      createGradingScaleRequest(apiModel);
      
    } catch (error) {
      notificationController.error({
        message: 'Error',
        description: 'Failed to create grading scale. Please check the form data.'
      });
      setIsSubmitting(false);
    }
  };

  // Effects
  useEffect(() => {
    if (deleteSuccess === true) {
      notificationController.success({
        message: 'Success',
        description: 'Grading scale deleted successfully!'
      });
      setDeleteModalVisible(false);
      setDeletingScale(null);
      getGradingScalesRequest();
    }
  }, [deleteSuccess]);

  // Handle update success
  useEffect(() => {
    if (updateSuccess) {
      notificationController.success({
        message: 'Success',
        description: 'Grading scale updated successfully!'
      });
      setIsSubmitting(false);
      handleEditModalClose();
      getGradingScalesRequest();
    }
  }, [updateSuccess]);

  // Handle update error
  useEffect(() => {
    if (updateError) {
      notificationController.error({
        message: 'Error',
        description: updateError || 'Failed to update grading scale'
      });
      setIsSubmitting(false);
    }
  }, [updateError]);
  
  useEffect(() => {
    if (createSuccess === true) {
      notificationController.success({
        message: 'Success',
        description: 'Grading scale created successfully!'
      });
      setIsSubmitting(false);
      handleCreateModalClose();
      resetForm();
      getGradingScalesRequest();
    }
  }, [createSuccess]);

  // Handle create error
  useEffect(() => {
    if (createError) {
      notificationController.error({
        message: 'Error',
        description: createError || 'Failed to create grading scale'
      });
      setIsSubmitting(false);
    }
  }, [createError]);

  useEffect(() => {
    // Load grading scales (userId already in localStorage from Keycloak)
    const storedUserId = localStorage.getItem("UserId");
    if (storedUserId && storedUserId !== 'null') {
      // Add userId to URL if not present
      if (!searchParams.get('userId')) {
        setSearchParams({ userId: storedUserId });
      }
      
      getGradingScalesRequest();
    }
  }, []);

  return (
    <>
      <PageTitle 
        title="Grading Scales" 
        description="Manage and create grading scales for your assessments" 
        url={window.location.href} 
        noIndex={true}
      />
      
      <PageContainer>
        {/* Header Section */}
        <HeaderSection>
          <TitleSection>
            <div>
              <h1 className="title">Grading Scales</h1>
              <p className="subtitle">Create and manage grading scales for consistent assessment</p>
            </div>
          </TitleSection>
          
          <ActionsSection>
            <SearchInput>
              <SearchOutlined className="search-icon" />
              <input
                type="text"
                placeholder="Search grading scales..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </SearchInput>
            
            <CreateButton
              variant="primary"
              businessSize="medium"
              onClick={handleCreateScale}
              icon={<PlusOutlined />}
            >
              Create New Scale
            </CreateButton>
          </ActionsSection>
        </HeaderSection>

        {/* Content Section */}
        <ContentCard>
          {loading ? (
            <LoadingState>
              {[1, 2, 3, 4].map((item) => (
                <SkeletonCard key={item}>
                  <div className="skeleton-line title"></div>
                  <div className="skeleton-line subtitle"></div>
                  <div style={{ marginTop: '16px' }}>
                    <div className="skeleton-line badge"></div>
                    <div className="skeleton-line badge"></div>
                    <div className="skeleton-line badge"></div>
                  </div>
                </SkeletonCard>
              ))}
            </LoadingState>
          ) : filteredScales.length > 0 ? (
            <ScalesGrid>
              {filteredScales.map((scale: any) => (
                <ScaleCard key={scale.id}>
                  <ScaleHeader>
                    <div>
                      <h3 className="scale-name">{scale.name}</h3>
                      <span className="scale-type">{scale.type}</span>
                    </div>
                  </ScaleHeader>
                  
                  <ScaleInfo>
                    <div className="created-date">
                      Created {formatDate(scale.createdAt)}
                    </div>
                  </ScaleInfo>
                  
                  <GradesContainer>
                    <div className="grades-label">Grades:</div>
                    <div className="grades-list">
                      {(scale.ranges || []).slice(0, 4).map((range: any, index: number) => (
                        <GradeBadge key={index} grade={range.grade}>
                          {range.grade} ({range.minValue}–{range.maxValue})
                        </GradeBadge>
                      ))}
                      {(scale.ranges || []).length > 4 && (
                        <GradeBadge grade="more">
                          +{scale.ranges.length - 4} more
                        </GradeBadge>
                      )}
                    </div>
                  </GradesContainer>
                  
                  <CardActions className="actions">
                    <ActionButton 
                      variant="edit" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditScale(scale.id);
                      }}
                    >
                      <EditOutlined />
                      Edit
                    </ActionButton>
                    <ActionButton 
                      variant="delete" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteScale(scale.id, scale.name);
                      }}
                    >
                      <DeleteOutlined />
                      Delete
                    </ActionButton>
                  </CardActions>
                </ScaleCard>
              ))}
            </ScalesGrid>
          ) : (
            <EmptyState>
              <span className="icon">🎓</span>
              <div className="title">
                {searchQuery ? 'No matching grading scales found' : 'No grading scales yet'}
              </div>
              <div className="description">
                {searchQuery 
                  ? `Try adjusting your search for "${searchQuery}"` 
                  : 'Create your first grading scale to get started with consistent assessment'
                }
              </div>
              {!searchQuery && (
                <BusinessButton
                  variant="primary"
                  businessSize="medium"
                  onClick={handleCreateScale}
                  icon={<PlusOutlined />}
                >
                  Create Your First Scale
                </BusinessButton>
              )}
            </EmptyState>
          )}
        </ContentCard>

        {/* Create/Edit Modal - using Portal to render outside PageContainer */}
        {createModalVisible && ReactDOM.createPortal(
          <ModalOverlay onClick={handleCreateModalClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <button className="close-btn" onClick={handleCreateModalClose}>×</button>
                <div className="title">Create New Grading Scale</div>
                <div className="subtitle">Define a new grading scale for consistent assessment</div>
              </ModalHeader>
              
              <ModalBody>
                {/* Scale Name */}
                <FormSection>
                  <FormField>
                    <label className="field-label">Scale Name</label>
                    <input
                      className="field-input"
                      type="text"
                      placeholder="e.g. Standard 100%, IB Scale, Pass/Fail"
                      value={formData.scaleName}
                      onChange={(e) => handleFormFieldChange('scaleName', e.target.value)}
                    />
                    {formErrors.scaleName && (
                      <div className="field-error">{formErrors.scaleName}</div>
                    )}
                  </FormField>
                </FormSection>

                {/* Scale Type */}
                <FormSection>
                  <div className="section-title">Type of Scale</div>
                  <RadioGroup>
                    <RadioOption>
                      <input
                        type="radio"
                        name="scaleType"
                        value="percentage"
                        checked={formData.scaleType === 'percentage'}
                        onChange={(e) => handleFormFieldChange('scaleType', e.target.value)}
                      />
                      <div>
                        <div className="option-label">Percentage</div>
                        <div className="option-description">0-100% based grading</div>
                      </div>
                    </RadioOption>
                    
                    <RadioOption>
                      <input
                        type="radio"
                        name="scaleType"
                        value="points"
                        checked={formData.scaleType === 'points'}
                        onChange={(e) => handleFormFieldChange('scaleType', e.target.value)}
                      />
                      <div>
                        <div className="option-label">Points</div>
                        <div className="option-description">Numeric point system</div>
                      </div>
                    </RadioOption>
                    
                    <RadioOption>
                      <input
                        type="radio"
                        name="scaleType"
                        value="custom"
                        checked={formData.scaleType === 'custom'}
                        onChange={(e) => handleFormFieldChange('scaleType', e.target.value)}
                      />
                      <div>
                        <div className="option-label">Custom Letters</div>
                        <div className="option-description">Custom grade letters</div>
                      </div>
                    </RadioOption>
                  </RadioGroup>
                </FormSection>

                {/* Grade Ranges */}
                <FormSection>
                  <div className="section-title">Grade Ranges</div>
                  <GradeTable>
                    <TableHeader>
                      <div>Grade</div>
                      <div>Min</div>
                      <div>Max</div>
                      <div>Description</div>
                      <div></div>
                    </TableHeader>
                    
                    {formData.gradeRanges.map((range) => {
                      const hasError = formErrors.gradeRanges?.[range.id];
  return (
                        <TableRow key={range.id} hasError={!!hasError}>
                          <input
                            className={`grade-input ${hasError ? 'error' : ''}`}
                            type="text"
                            placeholder="A"
                            value={range.grade}
                            onChange={(e) => handleGradeRangeChange(range.id, 'grade', e.target.value)}
                          />
                          <input
                            className={`range-input ${hasError ? 'error' : ''}`}
                            type={formData.scaleType === 'custom' ? 'text' : 'number'}
                            placeholder={formData.scaleType === 'custom' ? 'Min' : '90'}
                            value={range.min}
                            onChange={(e) => handleGradeRangeChange(range.id, 'min', e.target.value)}
                          />
                          <input
                            className={`range-input ${hasError ? 'error' : ''}`}
                            type={formData.scaleType === 'custom' ? 'text' : 'number'}
                            placeholder={formData.scaleType === 'custom' ? 'Max' : '100'}
                            value={range.max}
                            onChange={(e) => handleGradeRangeChange(range.id, 'max', e.target.value)}
                          />
                          <input
                            className="description-input"
                            type="text"
                            placeholder="Excellent"
                            value={range.description}
                            onChange={(e) => handleGradeRangeChange(range.id, 'description', e.target.value)}
                          />
                          <button
                            className="remove-btn"
                            onClick={() => removeGradeRange(range.id)}
                            disabled={formData.gradeRanges.length <= 1}
                            title="Remove row"
                          >
                            <MinusOutlined />
                          </button>
                        </TableRow>
                      );
                    })}
                  </GradeTable>
                  
                  <AddRowButton onClick={addGradeRange}>
                    <PlusOutlined />
                    Add Grade Range
                  </AddRowButton>
                  
                  {/* Validation Messages */}
                  {formErrors.gradeRanges && Object.values(formErrors.gradeRanges).some(error => error) && (
                    <ValidationMessage>
                      Please fix the highlighted grade range errors above
                    </ValidationMessage>
                  )}
                  
                  {formErrors.general && (
                    <ValidationMessage>
                      {formErrors.general}
                    </ValidationMessage>
                  )}
                </FormSection>

                {/* Visualization Preview */}
                <PreviewSection>
                  <div className="preview-title">Scale Preview</div>
                  <div className="preview-subtitle">
                    Visual representation of your grading scale
                  </div>
                  
                  {hasValidPreview ? (
                    <>
                      <GradientBar>
                        {previewData.map((segment) => (
                          <TooltipContainer key={segment.id} width={segment.width}>
                            <GradeSegment
                              color={segment.color}
                              isValid={segment.isValid}
                              onMouseEnter={() => setHoveredSegment(segment.id)}
                              onMouseLeave={() => setHoveredSegment(null)}
                            >
                              {segment.grade}
                            </GradeSegment>
                            <Tooltip visible={hoveredSegment === segment.id}>
                              {formData.scaleType === 'custom' 
                                ? `${segment.grade} → ${segment.description}`
                                : `${segment.min}–${segment.max} → Grade ${segment.grade} (${segment.description})`
                              }
                            </Tooltip>
                          </TooltipContainer>
                        ))}
                      </GradientBar>
                      
                      <PreviewStats>
                        <StatItem>
                          <div className="stat-value">{previewData.length}</div>
                          <div className="stat-label">Grade Levels</div>
                        </StatItem>
                        <StatItem>
                          <div className="stat-value">{formData.scaleType}</div>
                          <div className="stat-label">Scale Type</div>
                        </StatItem>
                        {formData.scaleType !== 'custom' && (
                          <>
                            <StatItem>
                              <div className="stat-value">
                                {Math.min(...previewData.map(p => parseFloat(p.min)))}
                              </div>
                              <div className="stat-label">Min Value</div>
                            </StatItem>
                            <StatItem>
                              <div className="stat-value">
                                {Math.max(...previewData.map(p => parseFloat(p.max)))}
                              </div>
                              <div className="stat-label">Max Value</div>
                            </StatItem>
                          </>
                        )}
                      </PreviewStats>
                    </>
                  ) : (
                    <EmptyPreview>
                      <span className="icon">📊</span>
                      <div className="message">
                        {!formData.scaleName.trim() 
                          ? 'Enter a scale name to see the preview'
                          : 'Add valid grade ranges to see the visualization'
                        }
                      </div>
                    </EmptyPreview>
                  )}
                </PreviewSection>
              </ModalBody>
              
              <ModalFooter>
                <FooterButton
                  variant="secondary"
                  businessSize="medium"
                  onClick={handleCreateModalClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </FooterButton>
                <FooterButton
                  variant="primary"
                  businessSize="medium"
                  onClick={handleFormSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Scale'}
                </FooterButton>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>,
          document.body
        )}

        {/* Edit Modal */}
        {editModalVisible && ReactDOM.createPortal(
          <ModalOverlay onClick={handleEditModalClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <button className="close-btn" onClick={handleEditModalClose}>×</button>
                <div className="title">Edit Grading Scale</div>
                <div className="subtitle">Modify your grading scale settings</div>
              </ModalHeader>
              
              <ModalBody>
                {/* Scale Name */}
                <FormSection>
                  <FormField>
                    <label className="field-label">Scale Name</label>
                    <input
                      className="field-input"
                      type="text"
                      placeholder="e.g. Standard 100%, IB Scale, Pass/Fail"
                      value={formData.scaleName}
                      onChange={(e) => handleFormFieldChange('scaleName', e.target.value)}
                    />
                    {formErrors.scaleName && (
                      <div className="field-error">{formErrors.scaleName}</div>
                    )}
                  </FormField>
                </FormSection>

                {/* Scale Type */}
                <FormSection>
                  <div className="section-title">Type of Scale</div>
                  <RadioGroup>
                    <RadioOption>
                      <input
                        type="radio"
                        name="editScaleType"
                        value="percentage"
                        checked={formData.scaleType === 'percentage'}
                        onChange={(e) => handleFormFieldChange('scaleType', e.target.value)}
                      />
                      <div>
                        <div className="option-label">Percentage</div>
                        <div className="option-description">0-100% based grading</div>
                      </div>
                    </RadioOption>
                    
                    <RadioOption>
                      <input
                        type="radio"
                        name="editScaleType"
                        value="points"
                        checked={formData.scaleType === 'points'}
                        onChange={(e) => handleFormFieldChange('scaleType', e.target.value)}
                      />
                      <div>
                        <div className="option-label">Points</div>
                        <div className="option-description">Numeric point system</div>
                      </div>
                    </RadioOption>
                    
                    <RadioOption>
                      <input
                        type="radio"
                        name="editScaleType"
                        value="custom"
                        checked={formData.scaleType === 'custom'}
                        onChange={(e) => handleFormFieldChange('scaleType', e.target.value)}
                      />
                      <div>
                        <div className="option-label">Custom Letters</div>
                        <div className="option-description">Custom grade letters</div>
                      </div>
                    </RadioOption>
                  </RadioGroup>
                </FormSection>

                {/* Grade Ranges */}
                <FormSection>
                  <div className="section-title">Grade Ranges</div>
                  <GradeTable>
                    <TableHeader>
                      <div>Grade</div>
                      <div>Min</div>
                      <div>Max</div>
                      <div>Description</div>
                      <div></div>
                    </TableHeader>
                    
                    {formData.gradeRanges.map((range) => {
                      const hasError = formErrors.gradeRanges?.[range.id];
                      return (
                        <TableRow key={range.id} hasError={!!hasError}>
                          <input
                            className={`grade-input ${hasError ? 'error' : ''}`}
                            type="text"
                            placeholder="A"
                            value={range.grade}
                            onChange={(e) => handleGradeRangeChange(range.id, 'grade', e.target.value)}
                          />
                          <input
                            className={`range-input ${hasError ? 'error' : ''}`}
                            type={formData.scaleType === 'custom' ? 'text' : 'number'}
                            placeholder={formData.scaleType === 'custom' ? 'Min' : '90'}
                            value={range.min}
                            onChange={(e) => handleGradeRangeChange(range.id, 'min', e.target.value)}
                          />
                          <input
                            className={`range-input ${hasError ? 'error' : ''}`}
                            type={formData.scaleType === 'custom' ? 'text' : 'number'}
                            placeholder={formData.scaleType === 'custom' ? 'Max' : '100'}
                            value={range.max}
                            onChange={(e) => handleGradeRangeChange(range.id, 'max', e.target.value)}
                          />
                          <input
                            className="description-input"
                            type="text"
                            placeholder="Excellent"
                            value={range.description}
                            onChange={(e) => handleGradeRangeChange(range.id, 'description', e.target.value)}
                          />
                          <button
                            className="remove-btn"
                            onClick={() => removeGradeRange(range.id)}
                            disabled={formData.gradeRanges.length <= 1}
                            title="Remove row"
                          >
                            <MinusOutlined />
                          </button>
                        </TableRow>
                      );
                    })}
                  </GradeTable>
                  
                  <AddRowButton onClick={addGradeRange}>
                    <PlusOutlined />
                    Add Grade Range
                  </AddRowButton>
                  
                  {formErrors.gradeRanges && Object.values(formErrors.gradeRanges).some(error => error) && (
                    <ValidationMessage>
                      Please fix the highlighted grade range errors above
                    </ValidationMessage>
                  )}
                  
                  {formErrors.general && (
                    <ValidationMessage>
                      {formErrors.general}
                    </ValidationMessage>
                  )}
                </FormSection>

                {/* Preview */}
                <PreviewSection>
                  <div className="preview-title">Scale Preview</div>
                  <div className="preview-subtitle">Visual representation of your grading scale</div>
                  
                  {hasValidPreview ? (
                    <>
                      <GradientBar>
                        {previewData.map((segment) => (
                          <TooltipContainer key={segment.id} width={segment.width}>
                            <GradeSegment
                              color={segment.color}
                              isValid={segment.isValid}
                            >
                              {segment.grade}
                            </GradeSegment>
                          </TooltipContainer>
                        ))}
                      </GradientBar>
                      
                      <PreviewStats>
                        <StatItem>
                          <div className="stat-value">{previewData.length}</div>
                          <div className="stat-label">Grade Levels</div>
                        </StatItem>
                        <StatItem>
                          <div className="stat-value">{formData.scaleType}</div>
                          <div className="stat-label">Scale Type</div>
                        </StatItem>
                      </PreviewStats>
                    </>
                  ) : (
                    <EmptyPreview>
                      <span className="icon">📊</span>
                      <div className="message">Add valid grade ranges to see preview</div>
                    </EmptyPreview>
                  )}
                </PreviewSection>
              </ModalBody>
              
              <ModalFooter>
                <FooterButton
                  variant="secondary"
                  businessSize="medium"
                  onClick={handleEditModalClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </FooterButton>
                <FooterButton
                  variant="primary"
                  businessSize="medium"
                  onClick={handleEditSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </FooterButton>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>,
          document.body
        )}

        {/* Delete Confirmation Modal */}
        {deleteModalVisible && ReactDOM.createPortal(
          <ModalOverlay onClick={handleDeleteModalClose}>
            <DeleteModalContent onClick={(e) => e.stopPropagation()}>
              <DeleteModalIcon>🗑️</DeleteModalIcon>
              <DeleteModalTitle>Delete Grading Scale</DeleteModalTitle>
              <DeleteModalText>
                Are you sure you want to delete <strong>"{deletingScale?.name}"</strong>?
                <br />
                This action cannot be undone.
              </DeleteModalText>
              <DeleteModalActions>
                <FooterButton
                  variant="secondary"
                  businessSize="medium"
                  onClick={handleDeleteModalClose}
                >
                  Cancel
                </FooterButton>
                <DeleteButton onClick={handleDeleteConfirm}>
                  <DeleteOutlined />
                  Delete Scale
                </DeleteButton>
              </DeleteModalActions>
            </DeleteModalContent>
          </ModalOverlay>,
          document.body
        )}
      </PageContainer>
    </>
  );
};

export default GradingScalesPage